import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  loadFileHandle, saveFileHandle, clearFileHandle,
  readFromFile, writeToFile, pickStorageFile, pickExistingStorageFile, isFileSystemSupported,
} from '../../utils/fileStorage';
import { applyPayload } from '../../utils/migrate';

/**
 * Manages File System Access API sync: persisted handle, unsaved-change tracking,
 * external-change polling, and explicit save/reload/link operations.
 *
 * @param {{ savePayload: object, savePayloadRef: React.MutableRefObject, setters: object, showToast: Function }} params
 */
export function useFileSync({ savePayload, setters, showToast }) {
  const [fileHandle,             setFileHandle]             = useState(null);
  const [fileName,               setFileName]               = useState(null);
  const [hasUnsavedChanges,      setHasUnsavedChanges]      = useState(false);
  const [externalChangeDetected, setExternalChangeDetected] = useState(false);

  const fileSyncEnabled      = !!fileHandle;
  const skipNextWriteRef     = useRef(false); // skip write when we just loaded from file
  const lastSavedModifiedRef = useRef(null);  // File.lastModified at last save/load

  // On mount: restore saved handle and load data from file if available
  useEffect(() => {
    let cancelled = false;
    async function init() {
      if (!isFileSystemSupported()) return;
      const handle = await loadFileHandle();
      if (!handle || cancelled) return;
      const data = await readFromFile(handle);
      if (!data || cancelled) return;
      try {
        const file = await handle.getFile();
        lastSavedModifiedRef.current = file.lastModified;
      } catch {}
      skipNextWriteRef.current = true;
      try {
        applyPayload(data, setters);
      } catch (e) {
        // Invalid file content — keep local data and don't link, so we never
        // overwrite the file with local state nor local state with garbage.
        skipNextWriteRef.current = false;
        showToast(`${handle.name} is ${e.message} — using browser data`, 'error');
        return;
      }
      setFileHandle(handle);
      setFileName(handle.name);
    }
    init();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mark unsaved whenever data changes (but skip the initial load-from-file pass)
  useEffect(() => {
    if (!fileHandle) return;
    if (skipNextWriteRef.current) { skipNextWriteRef.current = false; return; }
    setHasUnsavedChanges(true);
  }, [fileHandle, savePayload]);

  // Warn before closing the tab/window when there are unsaved changes
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // Poll for external file changes every 30 s
  useEffect(() => {
    if (!fileHandle) return;
    const id = setInterval(async () => {
      try {
        const file = await fileHandle.getFile();
        if (
          lastSavedModifiedRef.current !== null &&
          file.lastModified !== lastSavedModifiedRef.current
        ) {
          setExternalChangeDetected(true);
        }
      } catch {}
    }, 30_000);
    return () => clearInterval(id);
  }, [fileHandle]);

  // ─── Operations ───────────────────────────────────────────────────────────────

  /** Persist & wire up a freshly-picked handle. Internal helper. */
  const linkHandle = useCallback(async (handle) => {
    try {
      const file = await handle.getFile();
      lastSavedModifiedRef.current = file.lastModified;
    } catch {}
    await saveFileHandle(handle);
    setFileHandle(handle);
    setFileName(handle.name);
    setHasUnsavedChanges(false);
    setExternalChangeDetected(false);
  }, []);

  const saveToFile = useCallback(async () => {
    if (!fileHandle) return false;
    const ok = await writeToFile(fileHandle, savePayload);
    if (ok) {
      try {
        const file = await fileHandle.getFile();
        lastSavedModifiedRef.current = file.lastModified;
      } catch {}
      setHasUnsavedChanges(false);
      setExternalChangeDetected(false);
      showToast(`Saved to ${fileName}`, 'success');
    } else {
      showToast('Could not write to file', 'error');
    }
    return ok;
  }, [fileHandle, savePayload, fileName, showToast]);

  const reloadFromFile = useCallback(async () => {
    if (!fileHandle) return;
    const data = await readFromFile(fileHandle);
    if (!data) { showToast('Could not read file', 'error'); return; }
    try {
      const file = await fileHandle.getFile();
      lastSavedModifiedRef.current = file.lastModified;
    } catch {}
    skipNextWriteRef.current = true;
    try {
      applyPayload(data, setters);
    } catch (e) {
      skipNextWriteRef.current = false;
      showToast(`Could not reload: file is ${e.message}`, 'error');
      return;
    }
    setHasUnsavedChanges(false);
    setExternalChangeDetected(false);
    showToast('Reloaded from file');
  }, [fileHandle, showToast, setters]);

  const linkExistingFile = useCallback(async () => {
    const handle = await pickExistingStorageFile();
    if (!handle) return false;

    const existing = await readFromFile(handle);
    skipNextWriteRef.current = true;
    try {
      applyPayload(existing, setters);
    } catch (e) {
      skipNextWriteRef.current = false;
      showToast(`${handle.name} is ${e.message}. Use "Create new file" instead.`, 'error');
      return false;
    }

    await linkHandle(handle);
    showToast(`Loaded data from ${handle.name}`);
    return true;
  }, [showToast, linkHandle, setters]);

  const createNewFile = useCallback(async () => {
    const handle = await pickStorageFile();
    if (!handle) return false;
    const ok = await writeToFile(handle, savePayload);
    if (!ok) { showToast('Could not write to file', 'error'); return false; }
    await linkHandle(handle);
    showToast(`Linked to ${handle.name}`);
    return true;
  }, [savePayload, showToast, linkHandle]);

  const clearFileStorage = useCallback(async () => {
    await clearFileHandle();
    setFileHandle(null);
    setFileName(null);
    setHasUnsavedChanges(false);
    setExternalChangeDetected(false);
    showToast('Switched to browser storage');
  }, [showToast]);

  const dismissExternalChange = useCallback(() => setExternalChangeDetected(false), []);

  return useMemo(() => ({
    fileSyncEnabled, fileName, hasUnsavedChanges, externalChangeDetected,
    saveToFile, reloadFromFile, dismissExternalChange,
    setupFileStorage: linkExistingFile, // backwards-compat alias
    linkExistingFile, createNewFile, clearFileStorage,
  }), [
    fileSyncEnabled, fileName, hasUnsavedChanges, externalChangeDetected,
    saveToFile, reloadFromFile, dismissExternalChange,
    linkExistingFile, createNewFile, clearFileStorage,
  ]);
}
